import { IconArrowRight, IconCheck, IconRefresh } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import type { AmazonAuthorizationRow } from "@/lib/amazon-authorization";
import { cn } from "@/lib/utils";

/** Mirrors the matching `AmazonAuthorizationGuide` props so both levels stay in sync. */
interface AuthorizationStatusProps {
  adsError: boolean;
  canManage: boolean;
  spApiError: boolean;
  spApiAuthorizing?: boolean;
  onAuthorizeAds: () => void;
  onAuthorizeSpApi: (shopId: string, connectionId?: string) => void;
  onRetryAds: () => void;
  onRetrySpApi: () => void;
}

export function MerchantAuthorizationStatus({
  rows,
  adsError,
  canManage,
  spApiError,
  spApiAuthorizing,
  onAuthorizeAds,
  onAuthorizeSpApi,
  onRetryAds,
  onRetrySpApi,
}: Pick<
  AuthorizationStatusProps,
  | "adsError"
  | "canManage"
  | "spApiError"
  | "spApiAuthorizing"
  | "onAuthorizeAds"
  | "onAuthorizeSpApi"
  | "onRetryAds"
  | "onRetrySpApi"
> & { rows: readonly AmazonAuthorizationRow[] }): React.ReactElement {
  const { t } = useTranslation("settings");
  const adsConnected = rows.filter((row) => row.adsStatus === "connected").length;
  const regions = [...new Set(rows.map((row) => row.region))];

  return (
    <div className="grid items-center gap-x-3 gap-y-2 pl-6 sm:grid-cols-[112px_minmax(0,1fr)] md:pl-0">
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <span className="text-[10px] text-muted-foreground">
          {t("amazonAuthorization.table.adsApi")}
        </span>
        <div>
          <AuthorizationAction
            disabled={!canManage && !adsError}
            status={adsError ? "error" : adsConnected === rows.length ? "connected" : "attention"}
            label={t(
              adsError
                ? "amazonAuthorization.status.error"
                : adsConnected === rows.length
                  ? "amazonAuthorization.status.connected"
                  : "amazonAuthorization.actions.reauthorize",
            )}
            onClick={
              adsError ? onRetryAds : adsConnected === rows.length ? undefined : onAuthorizeAds
            }
          />
        </div>
        {!adsError && adsConnected > 0 && adsConnected < rows.length ? (
          <span className="text-warning-foreground">
            {t("amazonAuthorization.table.partialAuthorization", {
              connected: adsConnected,
              total: rows.length,
            })}
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <span className="text-[10px] text-muted-foreground">
          {t("amazonAuthorization.table.spApi")}
        </span>
        <SpApiAuthorizationStatus
          rows={rows}
          summaryOnly={regions.length > 1}
          canManage={canManage}
          spApiError={spApiError}
          spApiAuthorizing={spApiAuthorizing}
          onAuthorizeSpApi={onAuthorizeSpApi}
          onRetrySpApi={onRetrySpApi}
        />
      </div>
    </div>
  );
}

export function SpApiAuthorizationStatus({
  rows,
  summaryOnly = false,
  canManage,
  spApiError,
  spApiAuthorizing,
  onAuthorizeSpApi,
  onRetrySpApi,
}: Pick<
  AuthorizationStatusProps,
  "canManage" | "spApiError" | "spApiAuthorizing" | "onAuthorizeSpApi" | "onRetrySpApi"
> & { rows: readonly AmazonAuthorizationRow[]; summaryOnly?: boolean }): React.ReactElement | null {
  const { t } = useTranslation("settings");
  const target =
    rows.find((row) => row.spApiStatus === "attention") ??
    rows.find((row) => row.spApiStatus === "notConnected") ??
    rows[0];
  if (!target) return null;
  const connected = rows.filter((row) => row.spApiStatus === "connected").length;
  const partial = connected > 0 && connected < rows.length;

  if (spApiError)
    return (
      <div>
        <AuthorizationAction
          label={t("amazonAuthorization.status.error")}
          status="error"
          onClick={onRetrySpApi}
        />
      </div>
    );

  if (summaryOnly)
    return (
      <div>
        <AuthorizationAction
          status={partial ? "attention" : target.spApiStatus}
          label={
            partial
              ? t("amazonAuthorization.table.partialAuthorization", {
                  connected,
                  total: rows.length,
                })
              : t(`amazonAuthorization.status.${target.spApiStatus}`)
          }
        />
      </div>
    );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <AuthorizationAction
        disabled={!canManage || spApiAuthorizing}
        status={target.spApiStatus}
        label={t(
          target.spApiStatus === "connected"
            ? "amazonAuthorization.status.connected"
            : target.spApiStatus === "attention"
              ? "amazonAuthorization.actions.reauthorize"
              : "amazonAuthorization.table.authorizeNow",
        )}
        onClick={
          target.spApiStatus === "connected"
            ? undefined
            : () => {
                if (target.spApiConnection)
                  onAuthorizeSpApi(target.shop.shopId, target.spApiConnection.id);
                else onAuthorizeSpApi(target.shop.shopId);
              }
        }
      />
      {partial ? (
        <span className="text-warning-foreground">
          {t("amazonAuthorization.table.partialAuthorization", { connected, total: rows.length })}
        </span>
      ) : null}
    </div>
  );
}

function AuthorizationAction({
  disabled = false,
  label,
  onClick,
  status,
}: {
  disabled?: boolean;
  label: string;
  onClick?: () => void;
  status: "attention" | "connected" | "error" | "notConnected";
}): React.ReactElement {
  const icon =
    status === "connected" ? (
      <IconCheck className="size-3.5 text-success-foreground" stroke={2.4} aria-hidden />
    ) : status === "error" || status === "attention" ? (
      <IconRefresh className="size-3.5" aria-hidden />
    ) : (
      <IconArrowRight className="size-3.5" aria-hidden />
    );
  const content = (
    <>
      <span>{label}</span>
      {icon}
    </>
  );

  if (!onClick) {
    return <span className="inline-flex items-center gap-1.5 font-medium text-ink">{content}</span>;
  }

  if (status === "notConnected") {
    return (
      <Button size="xs" variant="outline" disabled={disabled} onClick={onClick}>
        {content}
      </Button>
    );
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-[6px] font-medium outline-none transition-colors hover:text-primary focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
        status === "attention" && "text-warning-foreground",
        status === "error" && "text-destructive-foreground",
      )}
    >
      {content}
    </button>
  );
}
